import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  Modifier,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button, Skeleton, Spinner } from "@heroui/react";
import { Plus } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";

import { AddBannerModal } from "@/components/banners/add-banner";
import { DeleteBannerModal } from "@/components/banners/delete-banner";
import { EditBannerModal } from "@/components/banners/edit-banner";
import {
  BANNER_ROW_GRID,
  SortableBannerRow,
} from "@/components/banners/sortable-banner-row";
import { notify } from "@/components/kit";
import { useGetAllBanners } from "@/hooks/api/banner/useGetAllBanners";
import { useReorderBanners } from "@/hooks/api/banner/useReorderBanners";
import { Banner } from "@/types";
import { getApiErrorMessage } from "@/utils/api-error";

const SKELETON_ROWS = 4;

const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

const BannerListSkeleton = () => (
  <ul>
    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
      <li
        key={index}
        className={`${BANNER_ROW_GRID} border-separator border-b py-3`}
      >
        <Skeleton className="size-4 rounded-md" />
        <Skeleton className="size-4 rounded-md" />
        <Skeleton className="aspect-[2/1] w-full rounded-xl" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-2/3 rounded-md" />
          <Skeleton className="h-3 w-1/2 rounded-md" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-8 w-16 rounded-lg justify-self-end" />
      </li>
    ))}
  </ul>
);

const Notice = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action: React.ReactNode;
}) => (
  <div className="flex flex-col items-center gap-1 py-16 text-center">
    <p className="font-medium">{title}</p>
    <p className="text-muted text-sm">{description}</p>
    <div className="mt-3">{action}</div>
  </div>
);

export const BannersPanel = ({
  actionSlot,
}: {
  /** The page header's action area: the Add banner button is drawn there. */
  actionSlot: HTMLElement | null;
}) => {
  const { data: banners, isPending, isError, refetch } = useGetAllBanners();
  const { mutate: reorderBanners, isPending: isSavingOrder } =
    useReorderBanners();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [deletingBanner, setDeletingBanner] = useState<Banner | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!banners || !over || active.id === over.id) return;

    const oldIndex = banners.findIndex((banner) => banner.id === active.id);
    const newIndex = banners.findIndex((banner) => banner.id === over.id);

    reorderBanners(arrayMove(banners, oldIndex, newIndex), {
      onError: (error) =>
        notify.error(
          "Failed to save banner order",
          getApiErrorMessage(error, "Please try again"),
        ),
    });
  };

  const renderContent = () => {
    if (isPending) return <BannerListSkeleton />;

    if (banners?.length) {
      return (
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          sensors={sensors}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={banners}
            strategy={verticalListSortingStrategy}
          >
            <ul>
              {banners.map((banner, index) => (
                <SortableBannerRow
                  key={banner.id}
                  banner={banner}
                  position={index + 1}
                  onDelete={setDeletingBanner}
                  onEdit={setEditingBanner}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      );
    }

    if (isError) {
      return (
        <Notice
          action={
            <Button size="sm" variant="secondary" onPress={() => refetch()}>
              Retry
            </Button>
          }
          description="The banners could not be loaded."
          title="Failed to load banners"
        />
      );
    }

    return (
      <Notice
        action={
          <Button size="sm" onPress={() => setIsAddModalOpen(true)}>
            Add banner
          </Button>
        }
        description="Add a banner to show it on the app and website home page."
        title="No banners yet"
      />
    );
  };

  return (
    <>
      {actionSlot
        ? createPortal(
            <Button onPress={() => setIsAddModalOpen(true)}>
              <Plus className="size-4" />
              Add Banner
            </Button>,
            actionSlot,
          )
        : null}

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <p className="text-muted text-sm">
              Drag to reorder, the top banner shows first
            </p>
            {isSavingOrder && (
              <span className="text-muted flex items-center gap-1.5 text-xs">
                <Spinner size="sm" />
                Saving order...
              </span>
            )}
          </div>
          {banners?.length ? (
            <span className="text-muted text-sm tabular-nums">
              {banners.length} {banners.length === 1 ? "banner" : "banners"}
            </span>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {banners?.length ? (
            <div
              aria-hidden="true"
              className={`${BANNER_ROW_GRID} text-muted border-separator border-b py-2 text-xs font-medium`}
            >
              <span />
              <span>#</span>
              <span>Banner</span>
              <span>Destination</span>
              <span>Promo code</span>
              <span />
            </div>
          ) : null}
          {renderContent()}
        </div>
      </div>

      <AddBannerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      <EditBannerModal
        banner={editingBanner}
        isOpen={!!editingBanner}
        onClose={() => setEditingBanner(null)}
      />

      <DeleteBannerModal
        banner={deletingBanner}
        isOpen={!!deletingBanner}
        onClose={() => setDeletingBanner(null)}
      />
    </>
  );
};
